import { useState, useMemo, useCallback } from 'react';
import { MicrostoreElementState, useGetColumnQuery, useGetColumnsPaginatedQuery } from 'data/__generated__';
import { Option, TotemSelect, TotemSelectProps } from 'components/TotemSelect';

type ColumnSelectProps = Omit<Extract<TotemSelectProps<string>, { isMulti?: false }>, 'value' | 'onChange'> & {
    value: string | null;
    onChange: (columnId: string | null) => void;
    prioritizeWarehouses?: boolean;
    states?: MicrostoreElementState[];
};

export function ColumnSelect({ value, onChange, prioritizeWarehouses, states, ...props }: ColumnSelectProps) {
    const [selectedOptionCache, setSelectedOptionCache] = useState<Option<string> | null>(null);

    const { data: currentColumnData } = useGetColumnQuery({
        variables: { _id: value! },
        skip: !value,
    });

    const { data: columnsData, refetch } = useGetColumnsPaginatedQuery({
        variables: {
            offset: 0,
            filters: {
                searchValue: '',
                prioritizeWarehouseTypes: prioritizeWarehouses,
                states,
            },
        },
        fetchPolicy: 'cache-and-network',
    });

    const defaultSearchOptions = useMemo(
        () =>
            (columnsData?.columnsFilter.columns || []).map((column) => ({
                label: column.fullname,
                value: column._id,
            })),
        [columnsData],
    );

    const currentColumnOption = useMemo(() => {
        if (currentColumnData?.column) {
            return {
                label: currentColumnData.column.fullname,
                value: currentColumnData.column._id,
            };
        }
        return selectedOptionCache;
    }, [currentColumnData, selectedOptionCache]);

    const defaultOptions = useMemo(() => {
        if (currentColumnOption && !defaultSearchOptions.find((opt) => opt.value === currentColumnOption.value)) {
            return [currentColumnOption, ...defaultSearchOptions];
        }
        return defaultSearchOptions;
    }, [defaultSearchOptions, currentColumnOption]);

    const handleChange = (columnId: string | null) => {
        const option = defaultOptions.find((opt) => opt.value === columnId) ?? null;
        if (option) {
            setSelectedOptionCache(option);
        }
        onChange(columnId);
    };

    const fetchOptions = useCallback(
        async (query: string): Promise<Option<string>[]> => {
            const result = await refetch({
                offset: 0,
                filters: { searchValue: query, prioritizeWarehouseTypes: prioritizeWarehouses, states },
            });
            return (result.data?.columnsFilter.columns || []).map((column) => ({
                label: column.fullname,
                value: column._id,
            }));
        },
        [refetch, prioritizeWarehouses, states],
    );

    return (
        <TotemSelect<string>
            placeholder="Rechercher une colonne..."
            value={value}
            onChange={handleChange}
            fetchOptions={fetchOptions}
            defaultOptions={defaultOptions}
            {...props}
        />
    );
}
